import { useEffect, useEffectEvent, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useTrackEvent } from '@/lib/posthog/hooks/useTrackEvent';
import { GoogleLogin } from '@/modules/auth/components/GoogleLogin';
import { LinkedInLogin } from '@/modules/auth/components/LinkedInLogin';
import { PasswordLogin } from '@/modules/auth/components/PasswordLogin';
import { SlackLogin } from '@/modules/auth/components/SlackLogin';
import {
  type AuthMethod,
  businessEmailRequiredMessage,
  getAuthFailureReason,
  isBusinessEmailRequiredError,
} from '@/modules/auth/constants';
import { useLinkedInSsoEnabled } from '@/modules/auth/hooks/useLinkedInSsoEnabled';
import { isSlackSsoConfigured } from '@/modules/auth/slackSso';
import {
  AuthLayout,
  OrDivider,
} from '@/modules/layouts/components/authLayout/AuthLayout';
import { TermsNotice } from '@/modules/legal/components/terms-notice/TermsNotice';
import { useLegalDocumentsQuery } from '@/modules/legal/queries/useLegalDocumentsQuery';
import { useCurrentUserQuery } from '@/modules/users/queries/useCurrentUserQuery';

// Generic on purpose: never confirm whether an email exists, and SSO
// failures land here too.
const LOGIN_FAILED_MESSAGE = 'Sign in failed. Please try again.';

/** "Google", "Google or Slack", "Google, Slack or LinkedIn". */
const formatProviders = (providers: (string | false)[]) =>
  new Intl.ListFormat('en-GB', { type: 'disjunction' }).format(
    providers.filter((provider) => provider !== false),
  );

const getSafeReturnPath = (value: string | null) => {
  if (!value) {
    return null;
  }

  let decoded: string;
  try {
    decoded = decodeURIComponent(value);
  } catch {
    decoded = value;
  }

  // Only allow internal, absolute paths. Reject protocol-relative/absolute
  // URLs (open redirect) and the login route itself, which would bounce the
  // user back here and cause an infinite redirect loop.
  const isInternalPath = decoded.startsWith('/') && !decoded.startsWith('//');
  if (!isInternalPath || decoded.toLowerCase().startsWith('/login')) {
    return null;
  }

  return decoded;
};

const Login = () => {
  const navigate = useNavigate();
  const trackEvent = useTrackEvent();
  const isLinkedInSsoEnabled = useLinkedInSsoEnabled();
  const terms = useLegalDocumentsQuery().data?.find(
    (document) => document.type === 'TermsAndConditions',
  );
  const acceptTerms = Boolean(terms?.url);

  const [searchParams] = useSearchParams();
  const safeReturnPath = getSafeReturnPath(searchParams.get('returnUrl'));
  const oauthReturnTo = safeReturnPath ?? undefined;

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const currentUserQuery = useCurrentUserQuery({
    enabled: isLoggedIn,
  });

  const onLoginSuccess = useEffectEvent(() => {
    if (safeReturnPath) {
      navigate(safeReturnPath, { replace: true });
      return;
    }

    const isHiringManager =
      currentUserQuery.data?.roles?.includes('HiringManager');
    const toPathname = isHiringManager ? '/hm' : '/';
    navigate(toPathname, { replace: true });
  });

  useEffect(() => {
    if (isLoggedIn && currentUserQuery.isSuccess && currentUserQuery.data) {
      onLoginSuccess();
    }
  }, [isLoggedIn, currentUserQuery.isSuccess, currentUserQuery.data]);

  const handleSuccess = (authMethod: AuthMethod) => {
    trackEvent('userSignedIn', {
      authMethod,
    });

    setError(null);
    setIsLoggedIn(true);
  };

  // Google, Slack and LinkedIn finish on their callback pages, so only a
  // sign-in that fails before leaving the page lands here.
  const handleError = (authMethod: AuthMethod, error?: Error) => {
    trackEvent('signInFailed', {
      authMethod,
      reason: getAuthFailureReason(error, authMethod),
    });
    setIsLoggedIn(false);
    setError(
      isBusinessEmailRequiredError(error)
        ? businessEmailRequiredMessage
        : LOGIN_FAILED_MESSAGE,
    );
  };

  return (
    <AuthLayout
      heading="Welcome back"
      subheading="Sign in to pick up where you left off."
      switchLink={{
        prompt: 'New to Finlay?',
        action: 'Create an account',
        to: '/sign-up',
      }}
    >
      <PasswordLogin
        error={error}
        onSuccess={() => handleSuccess('Email')}
        onError={(error) => handleError('Email', error)}
      />

      <OrDivider />

      <div className="space-y-2">
        <GoogleLogin
          returnTo={oauthReturnTo}
          acceptTerms={acceptTerms}
          intent="signIn"
          onError={(error) => handleError('Google', error)}
        />
        {isSlackSsoConfigured && (
          <SlackLogin
            returnTo={oauthReturnTo}
            acceptTerms={acceptTerms}
            intent="signIn"
            onError={(error) => handleError('Slack', error)}
          />
        )}
        {isLinkedInSsoEnabled && (
          <LinkedInLogin
            returnTo={oauthReturnTo}
            acceptTerms={acceptTerms}
            intent="signIn"
            onError={(error) => handleError('LinkedIn', error)}
          />
        )}
      </div>

      {/* Covers first sign-ins here too: a new account, or an invited or
          staff-created teammate claiming theirs. Password login never accepts. */}
      <TermsNotice
        lead={`By continuing with ${formatProviders([
          'Google',
          isSlackSsoConfigured && 'Slack',
          isLinkedInSsoEnabled && 'LinkedIn',
        ])}`}
        terms={terms}
      />
    </AuthLayout>
  );
};

export default Login;
